import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "@/routes/router";
import { loadPublicConfig } from "@/lib/config/public-config";
import { initializeTheme } from "@/lib/theme";
import { preferredLocale } from "@/lib/i18n";
import { loadPreferredCatalog } from "@/lib/i18n-catalogs";
import { retireLegacyServiceWorkers } from "@/lib/service-worker-retirement";

async function bootstrap() {
  // Resolve the theme before any awaited work: the production CSP forbids an
  // inline anti-FOUC script, so <html data-theme> has to be set here, ahead
  // of the first paint that render() triggers.
  initializeTheme();

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }

  // A client still controlled by the retired PWA must reload once after its
  // registration and caches are removed. The following navigation is then a
  // normal network request and cannot be answered by the obsolete app shell.
  const retirement = await retireLegacyServiceWorkers();
  if (retirement.controlled && retirement.registrationsRemoved > 0) {
    window.location.reload();
    return;
  }

  // Consumers read both synchronously, so they have to settle before the
  // first render rather than being awaited inside a component. Resolving the
  // locale up here means only the visitor's own catalog is on that path — the
  // provider would otherwise paint English first and fetch theirs after.
  await Promise.all([
    loadPublicConfig(),
    loadPreferredCatalog(preferredLocale()),
  ]);

  createRoot(rootElement).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

void bootstrap();
